// src/pages/black-friday-dashboard.tsx
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, useUser, UserButton } from "@clerk/clerk-react";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Loader2, Settings, RefreshCw } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { BFStorageData } from "@/types/black-friday";
import { fetchAllBFData } from "@/lib/black-friday-data-utils";
import { useBFAccounts } from "@/contexts/black-friday-account-context";
import { db } from "@/services/db";
import BlackFridayDashboardView from "@/components/black-friday/dashboard-view";
import BFAccountSelector from "@/components/black-friday/account-selector";
import { MainAppMigrationModal } from "@/components/black-friday/main-app-migration-modal";
import { MissingAccountBanner } from "@/components/black-friday/missing-account-banner";
import { BFOutdatedScriptAlert } from "@/components/black-friday/BFOutdatedScriptAlert";
import { REQUIRED_BF_SCRIPT_VERSION } from "@/lib/black-friday-constants";

export default function BlackFridayDashboardPage() {
  const navigate = useNavigate();
  const { isSignedIn } = useAuth();
  const { user } = useUser();
  const { accounts, currentAccount, updateAccount } = useBFAccounts();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bfData, setBfData] = useState<BFStorageData | null>(null);
  const [dataSource, setDataSource] = useState<"cache" | "fresh" | null>(null);
  const [cacheAge, setCacheAge] = useState<number | null>(null);

  // Redirect to setup if no accounts exist
  useEffect(() => {
    if (accounts.length === 0) {
      navigate("/bf/setup", { replace: true });
    }
  }, [accounts, navigate]);

  // Load data when current account changes
  useEffect(() => {
    if (currentAccount?.webAppUrl) {
      handleLoadData(currentAccount.webAppUrl);
    }
  }, [currentAccount?.id]);

  // Update account scriptVersion when data is fetched
  useEffect(() => {
    if (bfData?.dashboard?.scriptVersion && currentAccount) {
      const fetchedVersion = bfData.dashboard.scriptVersion;

      // Only update if version has changed
      if (currentAccount.scriptVersion !== fetchedVersion) {
        updateAccount(currentAccount.id, { scriptVersion: fetchedVersion });
      }
    }
  }, [bfData?.dashboard?.scriptVersion, currentAccount?.id]);

  const handleLoadData = async (url: string, forceRefresh = false) => {
    if (!url) {
      setError("No Web App URL found for this account");
      setIsLoading(false);
      return;
    }

    if (!currentAccount?.accountCID) {
      setError("No account Customer ID available");
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Check cache first (unless force refresh)
      if (!forceRefresh) {
        const cachedData = await db.getBFData(currentAccount.accountCID);

        if (cachedData) {
          const cacheTimestamp = new Date(cachedData.timestamp).getTime();
          const now = Date.now();
          const ageInHours = (now - cacheTimestamp) / (1000 * 60 * 60);

          // Use cache if less than 24 hours old
          if (ageInHours < 24) {
            console.log(`[BF Dashboard] Using cached data (${ageInHours.toFixed(1)}h old)`);
            setBfData(cachedData);
            setDataSource("cache");
            setCacheAge(ageInHours);
            setIsLoading(false);
            return;
          } else {
            console.log(`[BF Dashboard] Cache expired (${ageInHours.toFixed(1)}h old), fetching fresh data`);
          }
        }
      }

      // Fetch fresh data from Google Sheets
      console.log(`[BF Dashboard] Fetching fresh data from Google Sheets...`);
      const data = await fetchAllBFData(url);

      // Validate that we have minimum required data
      if (!data.dashboard) {
        throw new Error("No dashboard data found. Please ensure your script has run at least once.");
      }

      if (!data.dashboard.accountCID) {
        throw new Error("No account Customer ID found in dashboard data. Please run your script again.");
      }

      // Check if we have any performance data
      const hasData = data.dailyCampaign25?.length ||
                     data.dailyAllCampaigns25?.length ||
                     data.products25?.length ||
                     data.searchTerms25?.length;

      if (!hasData) {
        throw new Error("No Black Friday data found. Please ensure your script has collected data for the Black Friday period.");
      }

      // Save to cache
      await db.saveBFData(currentAccount.accountCID, data);
      console.log(`[BF Dashboard] Data saved to cache`);

      setBfData(data);
      setDataSource("fresh");
      setCacheAge(0);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to load data";
      setError(errorMessage);
      console.error("Error loading BF data:", err);
      setBfData(null); // Clear any partial data
      setDataSource(null);
      setCacheAge(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading state while checking accounts
  if (accounts.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  // Show loading state while fetching data
  if (isLoading || !bfData) {
    return (
      <div className="container max-w-4xl mx-auto py-12 px-4">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Loading Dashboard Data</h2>
          <p className="text-muted-foreground">
            Fetching Black Friday data for {currentAccount?.accountName}...
          </p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="container max-w-4xl mx-auto py-12 px-4">
        <Alert variant="destructive" className="mb-6">
          <AlertDescription className="text-base">{error}</AlertDescription>
        </Alert>

        <Card>
          <CardHeader>
            <CardTitle>What to do next</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h4 className="font-semibold mb-2">1. Check your script has run</h4>
              <p className="text-sm text-muted-foreground mb-2">
                Open your Google Ads account and check if the Black Friday script has run at least once.
                The script needs to run during or after the Black Friday period to collect data.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-2">2. Verify your sheet has data</h4>
              <p className="text-sm text-muted-foreground mb-2">
                Open your Google Sheet and check that it has been populated with data in the various tabs.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-2">3. Try again</h4>
              <div className="flex gap-3 mt-3">
                <Button onClick={() => currentAccount && handleLoadData(currentAccount.webAppUrl)}>
                  Retry Loading Data
                </Button>
                <Button variant="outline" onClick={() => navigate("/bf/settings")}>
                  Manage Accounts
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show dashboard
  return (
    <div className="w-full min-h-screen bg-muted">
      {/* Migration Modal for users coming from main app */}
      <MainAppMigrationModal />

      {/* Header Section */}
      <div className="container max-w-[1600px] mx-auto py-6 px-4">
        <div className="flex items-start justify-between mb-4 gap-4">
          {/* Left: Logo and Title */}
          <div className="flex items-center gap-4">
            <img src="/logo-light.png" alt="8020agent" className="h-12 w-auto" />
            <div>
              <h1 className="text-3xl font-bold">Black Friday Dashboard 2025</h1>
              <p className="text-muted-foreground">
                Comparing Black Friday 2025, 2024 & 2023
              </p>
            </div>
          </div>

          {/* Center: Account Selector */}
          <div className="flex items-center justify-center flex-1">
            <BFAccountSelector />
          </div>

          {/* Right: Settings and Auth */}
          <div className="flex items-center gap-3">
            {/* <CountdownTimer /> */}

            {/* Refresh Button with Cache Status */}
            {bfData && (
              <div className="flex flex-col items-end gap-1">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => currentAccount && handleLoadData(currentAccount.webAppUrl, true)}
                  disabled={isLoading}
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
                {dataSource === "cache" && cacheAge !== null && (
                  <span className="text-xs text-muted-foreground">
                    Cached {cacheAge < 1 ? `${Math.round(cacheAge * 60)}m` : `${Math.round(cacheAge)}h`} ago
                  </span>
                )}
                {dataSource === "fresh" && (
                  <span className="text-xs text-green-600 dark:text-green-400">
                    Just updated
                  </span>
                )}
              </div>
            )}

            {/* PROMINENT Settings Button */}
            {isSignedIn && (
              <Button
                size="sm"
                variant="default"
                onClick={() => navigate("/bf/settings")}
              >
                <Settings className="h-4 w-4 mr-2" />
                Settings
              </Button>
            )}

            {isSignedIn && user ? (
              <div className="relative z-9999 h-1 pt-2">
                <UserButton
                  appearance={{
                    elements: {
                      avatarBox: "w-8 h-8 flex items-center justify-center",
                    },
                  }}
                />
              </div>
            ) : (
              <Button size="sm" asChild>
                <a href="/auth/sign-in">Sign In / Sign Up</a>
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Dashboard Content */}
      <div className="container max-w-[1600px] mx-auto px-4 pb-12">
        {/* Missing Account Banner */}
        <MissingAccountBanner />

        {/* Outdated Script Alert */}
        <BFOutdatedScriptAlert
          accounts={accounts}
          requiredScriptVersion={REQUIRED_BF_SCRIPT_VERSION}
        />

        <BlackFridayDashboardView data={bfData} />
      </div>
    </div>
  );
}
